const parseScore = (review) => {
  const raw = review.stars ?? review.rating ?? review.star_value ?? review.score;
  const score = Number(raw);
  if (!Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(5, Math.round(score)));
};

const parseReviewList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.reviews)) return payload.reviews;
  return [];
};

const resolveReviewUrl = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return trimmed;
  return `/files/${trimmed}`;
};

// TL: Normalize ASIN values so product metafield and review payloads match reliably.
const normalizeAsin = (value) => {
  if (typeof value !== "string") return "";
  return value.trim().toUpperCase();
};

// TL: Merge Trustpilot and Amazon review feeds into one candidate pool.
const fetchSourceReviews = async (url, source) => {
  if (!url) return [];
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new Error(`${source} request failed with status ${response.status}`);
  }
  const payload = await response.json();
  return parseReviewList(payload).map((review) => ({
    ...review,
    source
  }));
};

const pickRandom = (items, count) => {
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
};

const selectWithTrustpilotGuarantee = (reviews, count) => {
  const safeCount = Math.max(1, count);
  const trustpilotReviews = reviews.filter((review) => review.source === "Trustpilot");
  if (!trustpilotReviews.length) {
    return pickRandom(reviews, safeCount);
  }

  const forcedTrustpilot = pickRandom(trustpilotReviews, 1);
  const forcedId = forcedTrustpilot[0]?.id;
  const remainingPool = reviews.filter((review) => {
    if (!forcedId) return true;
    return review.id !== forcedId;
  });
  const remaining = pickRandom(remainingPool, safeCount - 1);
  return pickRandom([...forcedTrustpilot, ...remaining], safeCount);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(document.documentElement.lang || "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(date);
};

const normalizeReviewUrl = (review) => {
  const raw =
    review.url ||
    review.review_url ||
    review.reviewUrl ||
    review.link ||
    review.href ||
    "";

  if (typeof raw !== "string") return "";
  const value = raw.trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (value.startsWith("/")) return `https://www.trustpilot.com${value}`;
  if (/^www\./i.test(value)) return `https://${value}`;
  return "";
};

const createStars = (score) => {
  const stars = document.createElement("div");
  stars.className = "tl-random-reviews__stars";
  stars.setAttribute("aria-label", `${score} out of 5 stars`);
  stars.textContent = `${"★".repeat(score)}${"☆".repeat(5 - score)}`;
  return stars;
};

const createCard = (review) => {
  const reviewUrl = normalizeReviewUrl(review);
  const isLink = Boolean(reviewUrl);
  const card = document.createElement(isLink ? "a" : "article");
  card.className = "tl-random-reviews__card";
  if (isLink) {
    card.classList.add("is-link");
    card.href = reviewUrl;
    card.target = "_blank";
    card.rel = "noopener noreferrer";
    card.setAttribute("aria-label", "Open full review in a new tab");
  }

  const meta = document.createElement("div");
  meta.className = "tl-random-reviews__meta";

  const name = document.createElement("div");
  name.className = "tl-random-reviews__name";
  name.textContent = review.name || review.author || "Anonymous";

  // TL: Show review source between author name and date.
  const source = document.createElement("div");
  source.className = "tl-random-reviews__source";
  source.textContent = review.source || "Unknown";

  const date = document.createElement("div");
  date.className = "tl-random-reviews__date";
  date.textContent = formatDate(review.date_posted || review.date || review.published_at);

  meta.append(name, source, date);

  const body = document.createElement("p");
  body.className = "tl-random-reviews__body";
  body.textContent = review.body || review.text || "";

  card.append(meta, createStars(parseScore(review)), body);
  return card;
};

const updateStatus = (element, text, hide = false) => {
  element.textContent = text;
  element.classList.toggle("is-hidden", hide);
};

const getReviewKey = (review) => {
  const id = review.id || review.review_id || review.reviewId || "";
  const date = review.date_posted || review.date || review.published_at || "";
  const name = review.name || review.author || "";
  const body = review.body || review.text || "";
  return [review.source || "", id, date, name, body].join("|");
};

// TL: Filter eligible reviews by age window configured in theme editor.
const isWithinOldestMonths = (review, oldestMonths) => {
  const rawDate = review.date_posted || review.date || review.published_at;
  if (!rawDate) return false;
  const publishedDate = new Date(rawDate);
  if (Number.isNaN(publishedDate.getTime())) return false;
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - oldestMonths);
  return publishedDate >= cutoff;
};

const initSection = async (section) => {
  const grid = section.querySelector("[data-tl-review-grid]");
  const status = section.querySelector("[data-tl-review-status]");
  // TL: Prefer Shopify-resolved file URLs from Liquid so plain filenames work across templates/previews.
  const trustpilotUrl =
    section.dataset.reviewFileResolvedUrl || resolveReviewUrl(section.dataset.reviewFileUrl);
  const amazonUrl =
    section.dataset.amazonFileResolvedUrl || resolveReviewUrl(section.dataset.amazonFileUrl);
  const productAsin = normalizeAsin(section.dataset.productAsin || "");
  const desktopCount = Math.max(1, Number(section.dataset.reviewCount) || 3);
  const mobileCount = Math.max(1, Number(section.dataset.mobileReviewCount) || desktopCount);
  const isMobile = window.matchMedia("(max-width: 749px)").matches;
  const count = isMobile ? mobileCount : desktopCount;
  const dwellSeconds = Math.max(
    2,
    Math.min(30, Number(section.dataset.carouselDwellSeconds) || 10)
  );
  const minScore = Math.max(1, Math.min(5, Number(section.dataset.minScore) || 4));
  const maxLines = Math.max(2, Math.min(12, Number(section.dataset.maxLines) || 3));
  const oldestMonths = Math.max(
    3,
    Math.min(24, Number(section.dataset.oldestMonths) || 24)
  );
  const configuredTransitionMs = Math.max(
    250,
    Math.min(2000, Number(section.dataset.carouselTransitionMs) || 1000)
  );
  section.style.setProperty("--tl-review-max-lines", String(maxLines));

  if (!trustpilotUrl && !amazonUrl) {
    updateStatus(status, "Add Trustpilot JSON and/or Amazon JSON in section settings.");
    return;
  }

  try {
    const [trustpilotResult, amazonResult] = await Promise.allSettled([
      fetchSourceReviews(trustpilotUrl, "Trustpilot"),
      fetchSourceReviews(amazonUrl, "Amazon")
    ]);

    const amazonReviews =
      amazonResult.status === "fulfilled"
        ? amazonResult.value.filter((review) => {
            if (!productAsin) return true;
            return normalizeAsin(review.asin || "") === productAsin;
          })
        : [];

    const mergedReviews = [
      ...(trustpilotResult.status === "fulfilled" ? trustpilotResult.value : []),
      ...amazonReviews
    ];

    const candidates = mergedReviews.filter((review) => {
      const score = parseScore(review);
      const body = review.body || review.text;
      return (
        score >= minScore &&
        typeof body === "string" &&
        body.trim().length > 0 &&
        isWithinOldestMonths(review, oldestMonths)
      );
    });

    if (!candidates.length) {
      if (trustpilotResult.status === "rejected" && amazonResult.status === "rejected") {
        throw new Error("Both review feeds failed to load.");
      }
      updateStatus(
        status,
        `No reviews within the last ${oldestMonths} months.`
      );
      return;
    }

    const pool = pickRandom(candidates, candidates.length);
    const trustpilotPool = pool.filter((review) => review.source === "Trustpilot");
    let start = Math.floor(Math.random() * pool.length);
    let trustpilotCursor = 0;

    const getFrame = () => {
      const frame = [];
      for (let i = 0; i < count; i += 1) {
        frame.push(pool[(start + i) % pool.length]);
      }

      if (!isMobile && trustpilotPool.length) {
        const hasTrustpilot = frame.some((review) => review.source === "Trustpilot");
        if (!hasTrustpilot) {
          const replacement = trustpilotPool[trustpilotCursor % trustpilotPool.length];
          trustpilotCursor += 1;
          const replaceIndex = Math.floor(Math.random() * frame.length);
          frame[replaceIndex] = replacement;
        }
      }
      return frame;
    };

    const initialFrame = getFrame();
    section.style.setProperty("--tl-visible-count", String(count));
    const track = document.createElement("div");
    track.className = "tl-random-reviews__track";
    track.append(...initialFrame.map((review) => createCard(review)));
    grid.replaceChildren(track);

    let currentFrame = initialFrame.slice();
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const transitionMs = prefersReducedMotion ? 0 : configuredTransitionMs;
    let isAnimating = false;

    const pickIncomingReview = (baseReview, keptReviews) => {
      if (isMobile || !trustpilotPool.length) return baseReview;
      const hasTrustpilot = keptReviews.some((review) => review.source === "Trustpilot");
      if (hasTrustpilot || baseReview.source === "Trustpilot") return baseReview;
      const forced = trustpilotPool[trustpilotCursor % trustpilotPool.length];
      trustpilotCursor += 1;
      return forced;
    };

    const advanceFrame = () => {
      if (isAnimating) return;
      isAnimating = true;

      start = (start + 1) % pool.length;
      const baseIncoming = pool[(start + count - 1) % pool.length];
      const keptReviews = currentFrame.slice(1);
      const incomingReview = pickIncomingReview(baseIncoming, keptReviews);
      const nextFrame = [...keptReviews, incomingReview];
      const outgoingCard = track.firstElementChild;
      if (!outgoingCard) {
        track.replaceChildren(...nextFrame.map((review) => createCard(review)));
        currentFrame = nextFrame;
        isAnimating = false;
        return;
      }

      track.appendChild(createCard(incomingReview));

      if (transitionMs === 0) {
        outgoingCard.remove();
        currentFrame = nextFrame;
        isAnimating = false;
        return;
      }

      const cardWidth = outgoingCard.getBoundingClientRect().width;
      const trackStyle = window.getComputedStyle(track);
      const gapValue = parseFloat(trackStyle.columnGap || trackStyle.gap || "0");
      const shiftDistance = Math.max(0, cardWidth + (Number.isFinite(gapValue) ? gapValue : 0));

      const finish = () => {
        track.removeEventListener("transitionend", onEnd);
        outgoingCard.remove();
        track.style.transition = "none";
        track.style.transform = "translateX(0)";
        void track.offsetWidth;
        currentFrame = nextFrame;
        isAnimating = false;
      };

      const onEnd = (event) => {
        if (event.target !== track || event.propertyName !== "transform") return;
        finish();
      };

      track.addEventListener("transitionend", onEnd);
      window.requestAnimationFrame(() => {
        track.style.transition = `transform ${transitionMs}ms ease`;
        track.style.transform = `translateX(-${shiftDistance}px)`;
      });
      window.setTimeout(finish, transitionMs + 80);
    };

    // TL: Pause carousel on hover and when tab is hidden to avoid distracting or wasted rotations.
    let intervalId = null;
    let hovered = false;

    const stopRotation = () => {
      if (!intervalId) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const tick = () => {
      advanceFrame();
    };

    const startRotation = () => {
      if (intervalId || pool.length <= count || hovered || document.hidden) return;
      intervalId = window.setInterval(tick, dwellSeconds * 1000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopRotation();
      } else {
        startRotation();
      }
    };

    section.addEventListener("mouseenter", () => {
      hovered = true;
      stopRotation();
    });

    section.addEventListener("mouseleave", () => {
      hovered = false;
      startRotation();
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);
    startRotation();

    updateStatus(status, "", true);
    section.classList.add("is-ready");
  } catch (error) {
    updateStatus(status, "Unable to load reviews right now.");
    console.error("TL random reviews failed to load", error);
  }
};

const initializeAll = () => {
  document.querySelectorAll("[data-tl-random-reviews]").forEach((section) => {
    initSection(section);
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeAll);
} else {
  initializeAll();
}
